import { ChangeEvent } from "react";
import { Col, Label, Row } from "reactstrap";
import { Checkbox, RadioGroup } from "../../../common/VFX-UI";
import NumericTextField from "../../../common/VFX-UI/TextField/NumericTextField";
import { GIFT_INVESTMENT_OPTION_OTHER_ID } from "../constants";
import { Options } from "../../../../types";
import { EmployeePledgeGiftInvestmentViewModel } from "../../../../viewModels/employeePledgeGiftInvestmentViewModel";
import { IsMobile } from "../../../../utility/lib/breakpoints";
import usePledgeStyles from "../styles";

interface ContributionValues {
    isNotWillingToContribute: boolean | null;
    contributionAmount: number | null;
    contributionOptionId: string | null;
    paymentOptionId: string | null;
    giftInvestment: Array<string | EmployeePledgeGiftInvestmentViewModel> | null;
    giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null;
    leadershipEnrollment?: boolean | null;
    stepUpEnrollment?: boolean | null;
}

interface ContributionSectionProps {
    values: ContributionValues;
    contributionOptions: Options[];
    pledgeStyles: ReturnType<typeof usePledgeStyles>;
    handleContributionOptionChange: (
        value: string,
        setFieldValue: ContributionSectionProps["setFieldValue"],
        giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null,
        paymentOptionId: string | null,
        formValues: ContributionValues
    ) => void | Promise<void>;
    setFieldValue: (field: string, value: unknown) => Promise<unknown>;
    setFieldTouched: (field: string) => Promise<unknown>;
    setOtherGiftOption: (pledgeAmount: number | null | undefined) => void;
    handleGiftInvestmentChange: (
        values: string[] | null,
        setFieldValue: ContributionSectionProps["setFieldValue"],
        contributionAmount: number | null,
        giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null
    ) => void;
    checkMinimumContribution: (
        contributionAmount: number,
        formValues: ContributionValues
    ) => void;
    enableOtherAnnualPledgeAmount: boolean;
}

const ContributionSection = (props: ContributionSectionProps) => {
    const isMobile = IsMobile();

    return (
    <Row style={{ minHeight: isMobile ? 0 : 500 }}>
        <Col sm={12} lg={6}>
            <Row>
                <Label
                    style={props.pledgeStyles.sectionHeader}
                    className="border-bottom mb-3 pb-2"
                >
                    1. Your Contribution Options
                </Label>
            </Row>

            {!props.values.isNotWillingToContribute &&
                props.contributionOptions && (
                    <RadioGroup
                        columns={1}
                        options={props.contributionOptions}
                        name="contributionOptionId"
                        onChange={async (event) => {
                            await props.handleContributionOptionChange(
                                event.target.value,
                                props.setFieldValue,
                                props.values.giftInvestmentAmount,
                                props.values.paymentOptionId,
                                props.values
                            );
                            await props.setFieldTouched("contributionAmount");
                        }}
                    />
                )}

            <div className="ms-3">
                <Checkbox
                    label="I do NOT wish to pledge/contribute"
                    name="isNotWillingToContribute"
                />
            </div>

            {!props.values.isNotWillingToContribute && (
                <Row>
                    <Label
                        style={props.pledgeStyles.sectionHeader}
                        className="border-bottom mb-3 pb-2"
                    >
                        Your Annual Contribution Pledge
                    </Label>
                    <Col style={{ display: "flex", alignItems: "center" }}>
                        <NumericTextField
                            isDollar
                            decimalPlace={0}
                            name="contributionAmount"
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                const contributionAmount = +event.target.value;
                                props.setOtherGiftOption(contributionAmount);

                                if (contributionAmount < 250) {
                                    props.setFieldValue(
                                        "giftInvestment",
                                        props.values.giftInvestment?.filter(
                                            (gift): gift is string =>
                                                typeof gift === "string" &&
                                                gift !== GIFT_INVESTMENT_OPTION_OTHER_ID
                                        )
                                    );
                                }

                                props.handleGiftInvestmentChange(
                                    null,
                                    props.setFieldValue,
                                    contributionAmount,
                                    props.values.giftInvestmentAmount
                                );
                            }}
                            onBlur={(handler) => {
                                const event = handler as unknown as ChangeEvent<HTMLInputElement>;
                                const contributionAmount = +event.target.value;
                                props.checkMinimumContribution(
                                    contributionAmount,
                                    props.values
                                );
                            }}
                            disabled={!props.enableOtherAnnualPledgeAmount}
                        />
                        <Label style={{ marginLeft: "10px" }}>
                            (Whole dollar amount only please)
                        </Label>
                    </Col>
                </Row>
            )}
        </Col>

        <Col
            className="d-none d-lg-block"
            style={props.pledgeStyles.backgroundImageStyle}
            lg={6}
        />
    </Row>
    );
};

export default ContributionSection;
